import NoDataForSelection from 'components/atoms/NoDataForSelection';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FilterContextData } from 'contexts/FilterContext';
import TileButton from 'components/atoms/TileButton';
import DownSvg from 'assets/svg/Down';
import styled from '@emotion/styled';
import { Modal, Select } from 'antd';
import tw from 'twin.macro';
import Button from 'components/atoms/Button';
import SelectionDropDown from 'components/molecules/SelectionDropDown';
import _ from 'lodash';
import AddEditEventModal from 'components/organism/PromoRecommendation/AddEditEventModal';
import { calculateFontSize, sendNotification } from 'utils/helperMethods';
import { CustomSelect } from 'CustomComponentCss';
import SaveScenarioModal from 'components/organism/PromoRecommendation/SaveScenarioModal';
import RecommendationMainSection from 'components/template/PromoRecommendation/RecommendationMainSection';
import useFetchGetPlans, { newPlanValue } from 'hooks/promoRecommendation/useFetchGetPlans';
import { promoRecommendation } from 'contexts/PromoRecommendationContext';
import { useSaveScenario } from 'hooks/promoRecommendation/useSaveScenario';
import SummarySection from 'components/template/PromoRecommendation/SummarySection';
import PlaybookSection from 'components/template/PromoRecommendation/PlaybookSection';
import { UserContext } from 'contexts/ConfigContext';
import { trackEventMixpanel } from 'utils/mixpanel';
import ImportEventModel from 'components/organism/PromoRecommendation/ImportEventModel';
import {
    PROMO_RECOMMENDATION_ADD_EVENT_CLICKED,
    PROMO_RECOMMENDATION_CALENDER_KPI_TAB_CHANGED,
    PROMO_RECOMMENDATION_MAIN_TAB_CHANGED,
    PROMO_RECOMMENDATION_NEW_SCENARIO_SELECTED,
    PROMO_RECOMMENDATION_SAVE_SCENARIO_CLICKED,
    PROMO_RECOMMENDATION_SELECT_SCENARIO_DROPDOWN_CHANGED,
} from 'utils/mixpanelEventNames';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import OptimizeScenarioModel from 'components/organism/PromoRecommendation/OptimizeScenarioModel';

export const commonClassName = 'border  border-borderLinePurpuleAlt p-4 rounded-lg';
export const commonHeaderClass = 'font-medium font-xl m-0 text-headerColor';

const scaledWidth = calculateFontSize(window.innerWidth, 13);
const ScaleInput = calculateFontSize(window.innerWidth, 200);

const typeSelection = [
    { key: 'Calender', label: 'Calender' },
    { key: 'KPI', label: 'KPI' },
];

export const CustomModalForSave = styled(Modal)`
    .ant-modal-close-x span svg {
        fill: black;
    }
    .ant-modal-title {
        font-size: '${scaledWidth}px';
        font-weight: 500;
    }
    .custom-modal .ant-modal-content {
        border-radius: 30px !important;
    }
`;

function PromoRecommendation() {
    const { filterNullError, allCheckedAttribute, idForRestOfComponent, allDimAndAttributeTypeList } =
        useContext(FilterContextData);
    const { appConfig: config } = useContext(UserContext);
    const { promoRecommendation: promoRecommendationConfig } = config;
    const menuButtons = promoRecommendationConfig.menuButtons;
    const retailerKey = promoRecommendationConfig.retailerKey;
    const kpiTableDataKeyMapping = promoRecommendationConfig.overlayKeys?.slice(4);
    const [activeMenuKey, setActiveMenuKey] = useState(menuButtons[0]);
    const [activeType, setActiveType] = useState(typeSelection[0]);
    const [saveScenarioModal, setSaveScenarioModal] = useState(false);
    const addEventData = config.promoRecommendation.addEventList;
    const hideShowOptimizeButton = config.promoRecommendation.hideShowOptimizeButton;
    const [eventList, setEventList] = useState(addEventData[0]);
    const [importDialogBox, setImportDialogBox] = useState(false);
    const [openOptimizeDialogBox, setOpenOptimizeDialogBox] = useState(false);
    const {
        selectedPlan,
        setSelectPlan,
        setLoadingForPlanList,
        setSelectedKpiList: setSelectedKPI,
        selectedKpiList: selectedKPI,
        addEditModal,
        fetchEventsList,
        setAddEditModal,
        PromotionMechanicValueData,
        eventsListData,
    } = useContext(promoRecommendation);

    const shouldShowOptimizeButton = selectedPlan?.planName && selectedPlan?.planName?.includes('opt_') ? false : true;
    const updateTypeRef: any = useRef();

    //kpi list shown in kpi table dropdown
    const kpiList = useMemo(() => {
        return kpiTableDataKeyMapping?.map((item: any) => {
            return {
                label: item?.label,
                value: item?.label,
            };
        });
    }, []);
    const { data: planData, loading: planLoading, error: planError, fetchPlan } = useFetchGetPlans();
    const {
        saveScenario,
        loading: saveScenarioLoading,
        error: saveScenarioError,
        setError: setSaveScenarioError,
    } = useSaveScenario({ onComplete: onSaveScenarioComplete });

    // Deduplicate plan data based on planId to avoid duplicate items in dropdown
    const modifiedPlanData = useMemo(() => {
        if (!planData || planData.length === 0) {
            return [];
        }

        const slicedData = planData.slice(1);
        const uniquePlans = new Map();

        slicedData.forEach((plan: any) => {
            if (plan && plan.planId && !uniquePlans.has(plan.planId)) {
                uniquePlans.set(plan.planId, plan);
            }
        });

        return Array.from(uniquePlans.values());
    }, [planData]);

    useEffect(() => {
        if (planData.length) {
            setSelectPlan(planData[1]);
        }
    }, [planData]);
    //only call api once during the load of screen and if retailer present
    useEffect(() => {
        if (allCheckedAttribute && Object.keys(allCheckedAttribute?.[retailerKey] || {})?.length)
            fetchPlan({
                variables: {
                    dim2AttributeName: `${Object.keys(allCheckedAttribute[retailerKey])[0]}`,
                },
            });
        //if no retailer present, show error message
        if (
            allCheckedAttribute &&
            retailerKey in allCheckedAttribute &&
            Object.keys(allCheckedAttribute?.[retailerKey] || {})?.length === 0
        ) {
            sendNotification('error', `Please select 1 ${retailerKey} from global filter`);
        }
    }, [idForRestOfComponent]);

    const combinedLoading = allDimAndAttributeTypeList?.loading || planLoading;

    useEffect(() => {
        setLoadingForPlanList(combinedLoading);
    }, [combinedLoading]);

    //called when saved scenario is completed
    function onSaveScenarioComplete() {
        setSaveScenarioModal(false);

        //if coming from add then fetch plan
        if (updateTypeRef.current === 'add') {
            //refetch the plan list to get updated plans
            fetchPlan({
                variables: {
                    dim2AttributeName: `${Object.keys(allCheckedAttribute[retailerKey])[0]}`,
                },
            });
        } else {
            //refetch the events list
            fetchEventsList({
                variables: {
                    planId: selectedPlan?.planId,
                },
            });
        }
    }

    function onOptimizeComplete() {
        fetchEventsList({
            variables: {
                planId: selectedPlan?.planId,
            },
        });
    }

    function fetchOptimizedPlan() {
        fetchPlan({
            variables: {
                dim2AttributeName: `${Object.keys(allCheckedAttribute[retailerKey])[0]}`,
            },
        });
    }

    //called when clicked on save button
    const onAddScenario = useCallback(
        (payload: any = {}) => {
            const tempObj = {
                plan_name: payload?.text,
                dim2_attribute_name: `${Object.keys(allCheckedAttribute[retailerKey])[0]}`,
                saved_type: 'final',
                year: payload?.year,
            };
            updateTypeRef.current = 'add';
            saveScenario({
                variables: {
                    operationType: 'add',
                    planId: selectedPlan?.planId,
                    metaData: JSON.stringify(tempObj),
                },
            });
        },
        [selectedPlan],
    );

    //called when clicked on save button
    const onSaveScenario = useCallback(() => {
        updateTypeRef.current = 'save';
        saveScenario({
            variables: {
                metaData: '{}',
                operationType: 'save',
                planId: selectedPlan?.planId,
            },
        });
    }, [selectedPlan]);

    return (
        <section className={` ${commonClassName} bg-white`}>
            {filterNullError ? (
                <NoDataForSelection />
            ) : (
                <div>
                    <div className="flex justify-start gap-4 items-center">
                        <h2 className={commonHeaderClass}>{'Promo Recommendation'}</h2>
                        <TileButton
                            disabled={combinedLoading}
                            data={menuButtons}
                            activeKey={activeMenuKey?.key}
                            onClick={(item: any) => {
                                setActiveMenuKey(item);
                                trackEventMixpanel(PROMO_RECOMMENDATION_MAIN_TAB_CHANGED, {
                                    tabName: item?.label,
                                });
                            }}
                        />
                    </div>
                    {activeMenuKey.key === menuButtons[0]?.key ? (
                        <>
                            <div className="mt-4 flex justify-between">
                                <div className="flex gap-3">
                                    <CustomSelect
                                        suffixIcon={<DownSvg />}
                                        //loading={allDimAndAttributeTypeList?.loading}
                                        value={selectedPlan}
                                        options={planData.map((item: any, index: number) => ({
                                            ...item,
                                            key: index,
                                        }))}
                                        className="dropdown-height dropdown-width-alt uniqSelPRScenerioDropdown"
                                        onChange={(val: any, item: any) => {
                                            setSelectPlan(item);
                                            trackEventMixpanel(PROMO_RECOMMENDATION_SELECT_SCENARIO_DROPDOWN_CHANGED, {
                                                dropdownName: item?.label,
                                            });
                                        }}
                                        onSelect={(value) => {
                                            if (value == 'newPlanPR') {
                                                setSaveScenarioModal(() => true);
                                                trackEventMixpanel(PROMO_RECOMMENDATION_NEW_SCENARIO_SELECTED);
                                            }
                                        }}
                                        loading={combinedLoading}
                                        disabled={combinedLoading}
                                        showSearch
                                        filterOption={(input, option) =>
                                            (option?.label ?? '')
                                                .toString()
                                                .toLowerCase()
                                                .startsWith(input.toLowerCase())
                                        }
                                    />
                                    <Button
                                        disabled={selectedPlan?.value === newPlanValue || !selectedPlan}
                                        title={'Save Scenario'}
                                        onClick={() => {
                                            trackEventMixpanel(PROMO_RECOMMENDATION_SAVE_SCENARIO_CLICKED);
                                            onSaveScenario();
                                        }}
                                    />
                                    {planData.length > 1 ? (
                                        <CustomSelect
                                            suffixIcon={<DownSvg />}
                                            value={eventList}
                                            options={addEventData.map((item: any, index: number) => ({
                                                ...item,
                                                key: index,
                                            }))}
                                            className="dropdown-height dropdown-width-alt uniqSelPRScenerioDropdown"
                                            onChange={(val: any, item: any) => {
                                                setEventList(item);
                                            }}
                                            onSelect={(value) => {
                                                if (value === 'new') {
                                                    setAddEditModal(true);
                                                    trackEventMixpanel(PROMO_RECOMMENDATION_ADD_EVENT_CLICKED);
                                                } else {
                                                    setImportDialogBox(true);
                                                }
                                            }}
                                        />
                                    ) : (
                                        ''
                                    )}
                                    {shouldShowOptimizeButton &&
                                        eventsListData &&
                                        Array.isArray(eventsListData?.eventsData) &&
                                        eventsListData?.eventsData?.length > 0 && (
                                            <Button
                                                title={'Optimize Scenario'}
                                                onClick={() => {
                                                    setOpenOptimizeDialogBox(true);
                                                }}
                                                theme={'Solid'}
                                            />
                                        )}
                                </div>
                                <div className="flex gap-3">
                                    {activeType.key == 'KPI' && (
                                        <SelectionDropDown
                                            list={kpiList}
                                            className="dropdown-width-alt"
                                            checkedListObject={selectedKPI}
                                            disabled={false}
                                            setSelectedList={setSelectedKPI}
                                            selectedList={selectedKPI}
                                        />
                                    )}
                                    <div className="flex items-center gap-2">
                                        <TileButton
                                            data={typeSelection}
                                            activeKey={activeType.key}
                                            onClick={(item: any) => {
                                                setActiveType(item);
                                                trackEventMixpanel(PROMO_RECOMMENDATION_CALENDER_KPI_TAB_CHANGED, {
                                                    tabName: item?.label,
                                                });
                                            }}
                                        />
                                        {eventsListData?.exportData && (
                                            <DownloadExcel
                                                title={'promo recommendation Data'}
                                                excelData={eventsListData?.exportData?.data ?? []}
                                                headers={eventsListData?.exportData?.header ?? []}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                            <RecommendationMainSection activeType={activeType} />
                        </>
                    ) : activeMenuKey.key === menuButtons[1]?.key ? (
                        <>
                            <div className="mt-4">
                                <SummarySection planData={planData} />
                            </div>
                        </>
                    ) : (
                        <div className="mt-4">
                            <PlaybookSection />
                        </div>
                    )}
                </div>
            )}
            {activeMenuKey.key === menuButtons[0]?.key && (
                <>
                    <AddEditEventModal
                        setAddEditModal={setAddEditModal}
                        addEditModal={addEditModal}
                        PromotionMechanicValueData={PromotionMechanicValueData}
                    />
                    <SaveScenarioModal
                        error={saveScenarioError ? String(saveScenarioError) : undefined}
                        loading={saveScenarioLoading}
                        onSave={onAddScenario}
                        setSaveScenario={setSaveScenarioModal}
                        saveScenario={saveScenarioModal}
                        title={'Add Plan'}
                        setError={setSaveScenarioError}
                    />
                </>
            )}

            <ImportEventModel
                selectedPlan={selectedPlan?.planId}
                modifiedPlanData={modifiedPlanData}
                importDialogBox={importDialogBox}
                setImportDialogBox={setImportDialogBox}
            />
            <OptimizeScenarioModel
                openOptimizeDialogBox={openOptimizeDialogBox}
                setOpenOptimizeDialogBox={setOpenOptimizeDialogBox}
                selectedPlan={selectedPlan}
                onOptimizeComplete={onOptimizeComplete}
                eventsListData={eventsListData}
                fetchOptimizedPlan={fetchOptimizedPlan}
            />
        </section>
    );
}

export default PromoRecommendation;
